// Правки справочника объектов: заказчик, договор, пост.
//
// Живут в разделе, а не в общем `shared/org/queries.ts`, по одной причине: общий файл читают и
// «Люди», и «Расчёт», а правит его сессия ядра целиком. Хуки правки нужны ровно трём панелям
// этого раздела, и держать их рядом с панелями — способ не расширять общую поверхность ради
// трёх вызовов. Чтение (`useCustomersQuery`, `useContractsQuery`, `usePostsQuery`) берётся
// оттуда: второй ключ кэша на те же данные дал бы два запроса на один список.
//
// Все три сбрасывают организационный кэш ЦЕЛИКОМ. Это не перестраховка: имя заказчика стоит в
// карточке объекта и в мастере нового объекта, номер договора — в выборе договора у объекта,
// часы поста — в расчётном нормативе объекта и в его укомплектованности. Сбросить только свой
// список значит оставить на соседнем экране прежнее написание того же самого.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import type {
  ContractOut,
  ContractPatch,
  CustomerOut,
  CustomerPatch,
  PostOut,
  PostPatch,
} from '@/shared/api/endpoints/org';
import { patchContract, patchCustomer, patchPost } from '@/shared/api/endpoints/org';
import type { ApiError } from '@/shared/errors/api-error';
import { orgKeys } from '@/shared/org/queries';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

export function usePatchCustomerMutation(
  customerId: string,
): UseMutationResult<CustomerOut, ApiError, CustomerPatch> {
  const client = useQueryClient();
  return useMutation<CustomerOut, ApiError, CustomerPatch>({
    mutationFn: (body) => patchCustomer(customerId, body),
    meta: подтверждение('Заказчик сохранён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function usePatchContractMutation(
  contractId: string,
): UseMutationResult<ContractOut, ApiError, ContractPatch> {
  const client = useQueryClient();
  return useMutation<ContractOut, ApiError, ContractPatch>({
    mutationFn: (body) => patchContract(contractId, body),
    meta: подтверждение('Договор сохранён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}

export function usePatchPostMutation(
  siteId: string,
  postId: string,
): UseMutationResult<PostOut, ApiError, PostPatch> {
  const client = useQueryClient();
  return useMutation<PostOut, ApiError, PostPatch>({
    mutationFn: (body) => patchPost(siteId, postId, body),
    meta: подтверждение('Пост сохранён'),
    onSuccess: () => {
      // Пост меняет и список постов, и расчётный норматив, и укомплектованность объекта.
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}
